"use client"
import React from 'react'
import HeaderMenu from '@/app/components/HeaderMenu'
import { BlurFade } from '@/app/components/BlurFade'
import { RedirectToMM } from '@/app/components/DeviceRedirect'
import { pick, type Locale } from '@/lib/i18n'
import { casesDict } from '@/lib/i18n/dicts/cases'

interface CasesClientProps {
    locale: Locale
    // 页脚为服务端组件（读库统计），由服务端页面渲染后传入
    footer: React.ReactNode
}

// 客户墙占位色块渐变（无真实 logo 阶段用首字母色块，按序循环取色）
const LOGO_GRADIENTS = [
    'from-indigo-500 to-fuchsia-500',
    'from-fuchsia-500 to-cyan-400',
    'from-cyan-400 to-indigo-500',
    'from-indigo-400 to-cyan-300',
    'from-fuchsia-400 to-indigo-600',
]

// 客户案例页：Hero → 客户墙 → 证言 → 接入数据 → CTA（无移动版，移动端跳 /mm/contact）
export default function CasesClient({ locale, footer }: CasesClientProps) {
    const t = pick(casesDict, locale)

    return (
        <>
            <RedirectToMM href="/mm/contact" />
            <HeaderMenu locale={locale} />

            {/* Hero */}
            <section className="relative overflow-hidden bg-slate-950">
                <div className="pointer-events-none absolute -top-48 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[130px]" />
                <div className="pointer-events-none absolute top-24 right-[-140px] h-[340px] w-[340px] rounded-full bg-cyan-400/10 blur-[110px]" />
                <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-12 text-center">
                    <BlurFade delay={0.05} inView>
                        <span className="inline-flex items-center gap-2 rounded-full ring-1 ring-indigo-400/30 bg-indigo-500/10 px-4 py-1.5 text-xs font-medium tracking-widest text-indigo-300 uppercase">
                            <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
                            {t.hero.badge}
                        </span>
                    </BlurFade>
                    <BlurFade delay={0.15} inView>
                        <h1 className="mt-6 text-4xl md:text-6xl font-black tracking-tight text-white">
                            {t.hero.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shine">
                                {t.hero.titleB}
                            </span>
                        </h1>
                    </BlurFade>
                    <BlurFade delay={0.25} inView>
                        <p className="mt-5 max-w-2xl mx-auto text-sm md:text-base leading-relaxed text-slate-400">
                            {t.hero.subtitle}
                        </p>
                    </BlurFade>
                </div>
            </section>

            {/* 客户墙 */}
            <section className="bg-slate-950 pb-20 pt-4">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade delay={0.05} inView>
                        <h2 className="text-center text-2xl md:text-4xl font-black tracking-tight text-white">
                            {t.wall.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent">{t.wall.titleB}</span>
                        </h2>
                    </BlurFade>
                    <BlurFade delay={0.15} inView>
                        <p className="mt-4 text-center text-xs md:text-sm text-slate-500">{t.wall.desc}</p>
                    </BlurFade>
                    <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        {t.wall.clients.map((client, i) => (
                            <BlurFade key={client.name} delay={0.05 * i} inView>
                                <div className="group h-full rounded-2xl bg-slate-900 ring-1 ring-white/10 p-5 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1 hover:ring-indigo-400/50 hover:shadow-xl hover:shadow-indigo-500/10">
                                    <span className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${LOGO_GRADIENTS[i % LOGO_GRADIENTS.length]} text-xl font-black text-white shadow-lg shadow-indigo-600/20 animate-breathe`}
                                        style={{ animationDelay: `${(i % 4) * 0.7}s` }}>
                                        {client.name.charAt(0)}
                                    </span>
                                    <p className="mt-3 text-sm font-bold text-white group-hover:text-indigo-300 transition-colors duration-300">{client.name}</p>
                                    <p className="mt-1 text-xs text-slate-500">{client.tag}</p>
                                </div>
                            </BlurFade>
                        ))}
                    </div>
                </div>
            </section>

            {/* 客户证言 */}
            <section className="bg-slate-950 pb-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade delay={0.05} inView>
                        <h2 className="text-center text-2xl md:text-4xl font-black tracking-tight text-white">
                            {t.testimonials.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent">{t.testimonials.titleB}</span>
                        </h2>
                    </BlurFade>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {t.testimonials.items.map((item, i) => (
                            <BlurFade key={item.author} delay={0.08 * i} inView>
                                <figure className="group h-full rounded-2xl bg-slate-900 ring-1 ring-white/10 p-6 flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:ring-indigo-400/50 hover:shadow-2xl hover:shadow-indigo-500/15">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7 text-indigo-400/60">
                                        <path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223ZM8.25 10.875a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25ZM10.875 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clipRule="evenodd" />
                                    </svg>
                                    <blockquote className="mt-4 flex-1 text-sm leading-6 text-slate-300">
                                        {item.quote}
                                    </blockquote>
                                    <figcaption className="mt-5 border-t border-white/5 pt-4">
                                        <p className="text-sm font-bold text-white">{item.author}</p>
                                        <p className="mt-0.5 text-xs text-slate-500">{item.role}</p>
                                    </figcaption>
                                </figure>
                            </BlurFade>
                        ))}
                    </div>
                </div>
            </section>

            {/* 接入数据 */}
            <section className="bg-slate-950 pb-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                        {t.stats.map((stat, i) => (
                            <BlurFade key={stat.label} delay={0.08 * i} inView>
                                <div className="rounded-2xl bg-slate-900 ring-1 ring-white/10 px-6 py-8 text-center transition-all duration-300 hover:-translate-y-1 hover:ring-indigo-400/50 hover:shadow-xl hover:shadow-indigo-500/10">
                                    <p className="text-3xl md:text-4xl font-black bg-gradient-to-br from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">
                                        {stat.value}
                                    </p>
                                    <p className="mt-2 text-sm text-slate-400">{stat.label}</p>
                                </div>
                            </BlurFade>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-slate-950 pb-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade delay={0.05} inView>
                        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 via-fuchsia-600 to-indigo-600 bg-[length:200%_auto] p-10 md:p-14 text-center shadow-2xl shadow-indigo-600/30">
                            <h2 className="text-2xl md:text-3xl font-black text-white">{t.cta.title}</h2>
                            <a href="../contact2"
                                className="mt-6 inline-block rounded-full bg-white px-8 py-3 text-sm font-bold text-indigo-700 transition-all duration-300 hover:scale-105 hover:shadow-xl">
                                {t.cta.button}
                            </a>
                        </div>
                    </BlurFade>
                </div>
            </section>

            {footer}
        </>
    )
}
